import { ErrorHandler, inject, Injectable, NgZone } from "@angular/core";
import { AdminCalibrationSubmitRequest, CalibrationHrComment, TalentMatrixUser, UserCalibrationData } from "alloy-performance-engine-cl";
import { API_REQUEST_METHODS, HttpService } from "../../../ancestors/base-api.service";
import { DownloadUtil } from "../../util/download.util";
import { ApeDownloadButtonService } from "../../components/ape-download-button/ape-download-button.service";

/**
  * Servizio per le API di calibration del backoffice
  */
@Injectable({ providedIn: "root" })
export class MonitoringCalibrationBackofficeApiService extends HttpService {
  private errorHandler = inject(ErrorHandler);

  private readonly downloadService = inject(ApeDownloadButtonService);

  private ngZone = inject(NgZone);

  constructor() {
    super();
    this.logger.setCaller("MonitoringCalibrationBackofficeApiService");
    this.createInstance({ url: this.env.apiBaseUrl });
  }

  /**
    * Recupera i dati completi per poter posizionare in matrice l'intero team dell'utente selezionato.
    * @param processId ID del processo
    * @param userId ID dell'utente per cui visualizzare il team nella calibration
    * @returns Promise con un array di TalentMatrixUser
    */
  async getCalibrationMatrixTeam(processId: string, userId: string): Promise<TalentMatrixUser[]> {
    try {
      this.logger.info(`Getting calibration matrix team for process ${processId} and user ${userId}`);

      const response = await this.httpClient<TalentMatrixUser[]>(`/backoffice/monitoring/calibration/matrix/team/${processId}/${userId}`, { method: API_REQUEST_METHODS.GET });

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Recupera gli utenti per la calibration matrix basandosi sui userIds memorizzati in sessionStorage.
    * Usato quando si accede con solo processId e la lista degli utenti è in sessionStorage.
    * @param processId ID del processo
    * @param userIds Array di userIds degli utenti da recuperare (da sessionStorage)
    * @returns Promise con un array di TalentMatrixUser
    */
  async getCalibrationMatrixUsersByIds(processId: string, userIds: string[]): Promise<TalentMatrixUser[]> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }

      if (!userIds || userIds.length === 0) {
        throw new Error("User IDs list is required");
      }

      this.logger.info(`Getting calibration matrix users for process ${processId} with ${userIds.length} user IDs from sessionStorage`);

      // Chiama l'endpoint con il processId nel path
      // Gli userIds vengono passati nel body come array
      const response = await this.httpClient<TalentMatrixUser[]>(`/backoffice/monitoring/calibration/matrix/users/${processId}`, {
        method: API_REQUEST_METHODS.POST,
        body: { userIds }
      });

      if (!response) {
        throw new Error("Invalid calibration matrix users response");
      }

      this.logger.info(`Retrieved ${response.length} calibration matrix users`);
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Il Commento di HR della scheda di un collaboratore, con lo storico delle modifiche (spec
    * Monitoraggio §9.5). La matrice di Calibration e' una simulazione e non salva piu' posizioni:
    * l'unico dato che l'amministratore scrive in Calibration e' questo commento.
    * @param processId ID del processo
    * @param userId ID del collaboratore valutato
    * @returns Promise con il commento, il permesso di modifica e lo storico
    */
  async getHrComment(processId: string, userId: string): Promise<CalibrationHrComment> {
    try {
      this.logger.info(`Getting HR comment for process ${processId} and user ${userId}`);

      const response = await this.httpClient<CalibrationHrComment>(`/backoffice/monitoring/calibration/hr-comment/${processId}/${userId}`, { method: API_REQUEST_METHODS.GET });

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Inserisce, modifica o rimuove (testo vuoto) il Commento di HR della scheda di un collaboratore
    * @param processId ID del processo
    * @param userId ID del collaboratore valutato
    * @param text Il testo del commento; vuoto lo rimuove
    * @returns Promise con il commento dopo il salvataggio
    */
  async saveHrComment(processId: string, userId: string, text: string): Promise<CalibrationHrComment> {
    try {
      this.logger.info(`Saving HR comment for process ${processId} and user ${userId}`);

      const response = await this.httpClient<CalibrationHrComment>(`/backoffice/monitoring/calibration/hr-comment/${processId}/${userId}`, {
        method: API_REQUEST_METHODS.POST,
        body: { text }
      });

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Conferma la fase di calibration dell'admin e invia ai manager le schede degli utenti.
    * @param request Richiesta di conferma e invio della calibration
    * @returns Promise con la lista aggiornata degli utenti della talent matrix
    */
  async submitCalibration(request: AdminCalibrationSubmitRequest): Promise<TalentMatrixUser[]> {
    try {
      this.logger.info(`Submitting calibration for process ${request.processId} with ${request.userIds.length} users`);

      const response = await this.httpClient<TalentMatrixUser[]>("/backoffice/monitoring/calibration/matrix/submit", {
        method: API_REQUEST_METHODS.POST,
        body: request
      });

      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
    * Esporta il report della Talent Matrix per la calibration
    * @param processId ID del processo
    * @param userIds Lista ID utenti da includere nel report
    * @param simulatedPositions Le posizioni correnti della simulazione delle persone Variate: non si
    * salvano, il report le usa solo per le sue colonne della simulazione
    * @param downloadId ID del download per verificare validità (opzionale)
    * @returns Promise con il file Excel
    */
  async exportCalibrationMatrix(processId: string, userIds: string[], simulatedPositions: UserCalibrationData[], downloadId: string): Promise<void> {
    try {
      this.logger.info(`Exporting calibration matrix for process ${processId} with ${userIds.length} users and ${simulatedPositions.length} simulated positions`);

      // Attivare per debug modale "Download in corso..."
      // await new Promise((resolve) => setTimeout(resolve, 10000));

      // Invia JSON per compatibilità con il backend che legge @Body("processId") e @Body("userIds")
      const response = await this.httpClient<{ fileName: string;
        fileUrl: string; }>("/backoffice/monitoring/calibration/matrix/export", {
        method: API_REQUEST_METHODS.POST,
        body: {
          processId,
          userIds,
          simulatedPositions
        },
        isFileDownload: true
      });

      // Verifica se il download è ancora valido usando l'ID specifico se fornito
      if (!this.downloadService.isDownloadValid(downloadId)) {
        this.logger.info(`Download ${downloadId} is no longer valid (cancelled or superseded), skipping file save`);
        return;
      }

      // Genera nome file di fallback
      const fallbackFilename = "talent-matrix.xlsx";

      // Usa l'utility per il download (form submit con target="_blank")
      DownloadUtil.downloadFromHttpServiceResponse(response, fallbackFilename);

      this.logger.info("Calibration matrix exported successfully");
    } catch (error) {
      this.logger.error("Error exporting calibration matrix:", String(error));
      this.errorHandler.handleError(error);
      throw error;
    }
  }
}
